import EditNoteIcon from "@mui/icons-material/Edit";
import EventIcon from "@mui/icons-material/Event";
import PhoneInTalkIcon from "@mui/icons-material/PhoneInTalk";
import {
  Alert,
  Box,
  Button,
  Chip,
  Stack,
  Typography,
} from "@mui/material";
import PropTypes from "prop-types";
import { useMemo } from "react";

import { toJalaliFullDate } from "../../middleware/middleware";
import {
  summarizeFollowUpFromRow,
  summarizeFollowUps,
} from "./followUpReminderUtils";

const FollowUpReminderBanner = ({
  followUps,
  rowSummary,
  onRegisterClick,
  showRegisterButton = true,
  compact = false,
}) => {
  const summary = useMemo(() => {
    if (Array.isArray(followUps)) return summarizeFollowUps(followUps);
    if (rowSummary) return summarizeFollowUpFromRow(rowSummary);
    return summarizeFollowUps([]);
  }, [followUps, rowSummary]);

  const {
    followUpCount,
    nextFollowUpDate,
    followUpReminderStatus,
    followUpReminderTitle,
    alertSeverity,
  } = summary;

  const hasNextDate = Boolean(nextFollowUpDate);
  const showAction = showRegisterButton && typeof onRegisterClick === "function";
  const alertSx = {
    mb: compact ? 1 : 2,
    py: compact ? 0.25 : 0.5,
    "& .MuiAlert-message": { py: compact ? 0.25 : 0.5 },
    "& .MuiAlert-action": { alignItems: "center", pt: 0 },
  };

  if (followUpCount <= 0) {
    if (!showAction) return null;
    return (
      <Alert
        severity="info"
        icon={<PhoneInTalkIcon sx={{ fontSize: compact ? 18 : 20 }} />}
        action={
          <Button
            color="inherit"
            size="small"
            startIcon={<EditNoteIcon fontSize="small" />}
            onClick={onRegisterClick}
            sx={{ fontSize: "0.75rem", py: 0.25 }}
          >
            ثبت پیگیری
          </Button>
        }
        sx={alertSx}
      >
        <Typography variant={compact ? "caption" : "body2"} fontWeight={700} sx={{ mb: 0.25 }}>
          پیگیری مشتری
        </Typography>
        <Typography variant="caption" color="text.secondary">
          هنوز پیگیری‌ای ثبت نشده است. پس از تماس، نتیجه و در صورت نیاز تاریخ تماس
          بعدی را ثبت کنید.
        </Typography>
      </Alert>
    );
  }

  const message =
    followUpReminderStatus === "overdue"
      ? "موعد پیگیری گذشته است — لطفاً در اسرع وقت با متقاضی تماس بگیرید."
      : followUpReminderStatus === "today"
        ? "امروز یا فردا موعد پیگیری است."
        : hasNextDate
          ? "پیگیری بعدی در تاریخ زیر برنامه‌ریزی شده است."
          : "آخرین پیگیری ثبت شده است؛ تاریخ تماس بعدی تعیین نشده.";

  return (
    <Alert
      severity={alertSeverity || "info"}
      icon={<PhoneInTalkIcon sx={{ fontSize: compact ? 18 : 20 }} />}
      action={
        showAction ? (
          <Button
            color="inherit"
            size="small"
            variant="outlined"
            startIcon={<EditNoteIcon fontSize="small" />}
            onClick={onRegisterClick}
            sx={{ whiteSpace: "nowrap", fontSize: "0.75rem", py: 0.25 }}
          >
            ثبت پیگیری جدید
          </Button>
        ) : null
      }
      sx={alertSx}
    >
      <Stack spacing={compact ? 0.25 : 0.75}>
        <Stack direction="row" flexWrap="wrap" alignItems="center" sx={{ gap: 0.5 }}>
          <Typography variant={compact ? "caption" : "body2"} fontWeight={700}>
            یادآوری پیگیری
          </Typography>
          {followUpReminderTitle ? (
            <Chip
              size="small"
              label={followUpReminderTitle}
              color={alertSeverity}
              sx={{ height: 22, fontSize: "0.7rem" }}
            />
          ) : null}
          <Chip
            size="small"
            variant="outlined"
            label={`${followUpCount} پیگیری`}
            sx={{ height: 22, fontSize: "0.7rem" }}
          />
        </Stack>
        <Typography variant="caption">{message}</Typography>
        {hasNextDate ? (
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            <EventIcon sx={{ fontSize: 14, color: "inherit", opacity: 0.8 }} />
            <Typography variant="caption" fontWeight={700}>
              پیگیری بعدی: {toJalaliFullDate(nextFollowUpDate)}
            </Typography>
          </Box>
        ) : null}
      </Stack>
    </Alert>
  );
};

FollowUpReminderBanner.propTypes = {
  followUps: PropTypes.array,
  rowSummary: PropTypes.object,
  onRegisterClick: PropTypes.func,
  showRegisterButton: PropTypes.bool,
  compact: PropTypes.bool,
};

export default FollowUpReminderBanner;
